import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { Link } from '@sorare/routing';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Target } from '@sorare/core/src/atoms/icons/Target';
import { iconAddPlayer } from '@sorare/core/src/atoms/icons/system/iconAddPlayer.ts';
import { iconChevronDoubleTop } from '@sorare/core/src/atoms/icons/system/iconChevronDoubleTop.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BodyM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { range } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { useGetCardSetConfig } from '@sorare/core/src/set';
import { mediumMobileHeightAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ReplayTitle } from '#src/components/series/ReplayTitle/index.tsx';

import type { ReplayIntroDialog_taskInterface } from './__generated__/index.graphql.ts';

const StyledDialog = styled(Dialog)`
  background: var(--background-image) no-repeat center center;
  background-size: cover;
`;

const Content = styled(Vertical).attrs({ center: true })`
  height: 100%;
  position: relative;
  padding: calc(6 * var(--unit)) var(--double-unit);
  gap: calc(6 * var(--unit));

  @media ${mediumMobileHeightAndAbove} {
    gap: calc(10 * var(--unit));
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const Placeholder = styled(Vertical)`
  flex: 1;
  border-radius: var(--unit);
  aspect-ratio: var(--card-aspect-ratio);
  border: 1px solid var(--c-set-surface-primary);
  background: rgba(var(--c-rgb-set-surface-primary), 0.16);
  backdrop-filter: blur(12px);
  max-width: 160px;
  margin: 0 auto;
  align-items: center;
  justify-content: center;
`;

const Cards = styled(Horizontal)`
  width: 100%;
`;

type Props = {
  onClose: () => void;
  open: boolean;
  task: ReplayIntroDialog_taskInterface;
  to: string;
};
export const ReplayIntroDialog = ({ task, open, onClose, to }: Props) => {
  const { sport } = useSportContext();
  const getCardSetConfig = useGetCardSetConfig();
  const { visual } = getCardSetConfig();

  if (!isType(task, 'ThresholdPickerTask')) {
    return null;
  }
  const cardsCount =
    (task.displayedTypedRules ?? []).filter(isTypeFn('So5AppearancesRule'))[0]
      ?.appearances?.length ?? 0;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      style={
        {
          '--background-image': `url(${visual.replayDialog.background[sport]})`,
        } as CSSProperties
      }
      hideHeader
    >
      {({ CloseButton }) => (
        <Content>
          <CloseButtonWrapper>
            <CloseButton onClose={onClose} />
          </CloseButtonWrapper>
          <Vertical gap={3} center>
            <ReplayTitle />
            <BodyL className="text-center">
              <FormattedMessage
                defaultMessage="Pick {playerCount, plural, one {# player} other {# players}} from your collection who scored more than {score} PTS {playerCount, plural, one {in their} other {combined in their}} <em>last match</em>."
                values={{
                  playerCount: cardsCount,
                  em: chunks => (
                    <BodyL as="span" bold color="var(--c-rewards-yellow)">
                      {chunks}
                    </BodyL>
                  ),
                  score: (
                    <>
                      <Target /> {task.target}
                    </>
                  ),
                }}
              />
            </BodyL>
          </Vertical>
          <Cards>
            {range(cardsCount).map(index => (
              <Placeholder key={index} as={Link} to={to}>
                <FontAwesomeIcon icon={iconAddPlayer} size="sm" />
                <FormattedMessage {...glossary.add} />
              </Placeholder>
            ))}
          </Cards>
          <Vertical>
            <FontAwesomeIcon
              color="var(--c-set-surface-primary)"
              icon={iconChevronDoubleTop}
            />
            <BodyM>
              <FormattedMessage defaultMessage="Add your players above" />
            </BodyM>
          </Vertical>
        </Content>
      )}
    </StyledDialog>
  );
};

ReplayIntroDialog.fragments = {
  taskInterface: gql`
    fragment ReplayIntroDialog_taskInterface on TaskInterface {
      id
      target
      ... on ThresholdPickerTask {
        id
        displayedTypedRules {
          id
          ... on So5AppearancesRule {
            id
            appearances {
              id
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<ReplayIntroDialog_taskInterface>,
};
